import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";
import OnboardingChecklist from "@/components/dashboard/OnboardingChecklist";

const currency = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const KPI = ({ label, value, sub }) => (
  <Card className="p-6 border border-navy/10 rounded-none bg-white" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="label-eyebrow text-navy/50">{label}</div>
    <div className="serif-display text-4xl md:text-5xl text-navy mt-3">{value}</div>
    {sub && <div className="text-xs text-navy/50 mt-1">{sub}</div>}
  </Card>
);

export default function Overview() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [apps, setApps] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        if (user.role === "admin" || user.role === "client") {
          const s = await api.get("/payroll/summary");
          setSummary(s.data);
        }
        if (user.role === "admin" || user.role === "candidate") {
          const a = await api.get("/applications");
          setApps(a.data);
        }
      } catch (_) {}
    })();
  }, [user]);

  return (
    <div className="space-y-10" data-testid="overview-page">
      <div>
        <div className="label-eyebrow text-navy/50">{user.role} · overview</div>
        <h1 className="serif-display text-4xl md:text-5xl mt-3">Good day, {user.name?.split(" ")[0]}.</h1>
      </div>

      {user.role !== "candidate" && summary && (
        <div className="grid md:grid-cols-4 gap-6">
          <KPI label="Manpower on payroll" value={summary.total_manpower?.toLocaleString("en-IN")} sub={`${summary.clients} clients`} />
          <KPI label="Payroll records" value={summary.total_records?.toLocaleString("en-IN")} />
          <KPI label="Gross disbursed" value={currency(summary.total_gross)} sub="All-time" />
          <KPI label="Net disbursed" value={currency(summary.total_net)} sub="Post statutory" />
        </div>
      )}

      {user.role === "candidate" && (
        <>
          <div className="grid md:grid-cols-3 gap-6">
            <KPI label="Applications" value={apps.length} sub="Submitted by you" />
            <KPI label="Under review" value={apps.filter(a => a.status === "submitted").length} />
            <KPI label="Matched score avg" value={apps.length ? Math.round(apps.reduce((s,a)=>s+(a.match_score||0),0)/apps.length) : "—"} sub="Across AI screens" />
          </div>
          <OnboardingChecklist applicationsCount={apps.length} />
        </>
      )}

      {summary?.monthly?.length > 0 && (user.role !== "candidate") && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="p-6 rounded-none border-navy/10 bg-white">
            <div className="label-eyebrow text-navy/50">Monthly gross</div>
            <div className="h-64 mt-4">
              <ResponsiveContainer>
                <LineChart data={summary.monthly}>
                  <XAxis dataKey="period" stroke="#64748B" fontSize={11}/>
                  <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v)=>`₹${(v/100000).toFixed(0)}L`}/>
                  <Tooltip formatter={(v)=>currency(v)} contentStyle={{ background: "#14198C", border: "none", color: "#F8F7FE" }}/>
                  <Line type="monotone" dataKey="gross" stroke="#14198C" strokeWidth={2} dot={{ r: 3, fill: "#6B39E5" }}/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card className="p-6 rounded-none border-navy/10 bg-white">
            <div className="label-eyebrow text-navy/50">Monthly net</div>
            <div className="h-64 mt-4">
              <ResponsiveContainer>
                <BarChart data={summary.monthly}>
                  <CartesianGrid vertical={false} stroke="#E2E8F0"/>
                  <XAxis dataKey="period" stroke="#64748B" fontSize={11}/>
                  <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v)=>`₹${(v/100000).toFixed(0)}L`}/>
                  <Tooltip formatter={(v)=>currency(v)} contentStyle={{ background: "#14198C", border: "none", color: "#F8F7FE" }}/>
                  <Bar dataKey="net" fill="#D4AF37" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
